import type { JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api.js";
import { fmtPct } from "../format.js";
import { Money } from "../components/Money.js";
import { PageHeader } from "../components/PageHeader.js";
import { StatCard } from "../components/StatCard.js";
import { Gauge } from "../components/Gauge.js";
import { MonthlySpend } from "../components/MonthlySpend.js";

const STAT_GRID = "grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4";

export function Dashboard(): JSX.Element {
  const nw = useQuery({ queryKey: ["nw"], queryFn: () => api.netWorth() });
  const fs = useQuery({ queryKey: ["fs"], queryFn: () => api.fireScores() });
  const ef = useQuery({ queryKey: ["ef"], queryFn: () => api.emergencyFund() });

  return (
    <>
      <PageHeader title="Dashboard" />
      <div className={STAT_GRID}>
        <StatCard
          label="Net worth"
          value={nw.data ? <Money amountMinor={nw.data.total_eur_minor} native="EUR" block /> : "—"}
          sub={nw.data ? `as of ${new Date(nw.data.as_of).toLocaleString()}` : ""}
        />
        <StatCard
          label="Emergency fund"
          value={
            ef.data
              ? ef.data.has_spend_data
                ? `${ef.data.months_runway.toFixed(1)} months`
                : "No spend data yet"
              : "—"
          }
          sub={
            ef.data ? (
              ef.data.has_spend_data ? (
                <>
                  liquid <Money amountMinor={ef.data.liquid_eur_minor} native="EUR" /> / burn{" "}
                  <Money amountMinor={ef.data.monthly_burn_eur_minor} native="EUR" />
                </>
              ) : (
                <>
                  liquid <Money amountMinor={ef.data.liquid_eur_minor} native="EUR" /> · runway needs
                  real spend transactions
                </>
              )
            ) : (
              ""
            )
          }
        />
      </div>

      <MonthlySpend />

      <h3 className="mt-8 mb-4 text-lg font-semibold">FIRE progress</h3>
      <div className={STAT_GRID}>
        {fs.data?.scores.map((s) => (
          <StatCard key={s.target_id} label={s.label} value={fmtPct(s.score)}>
            <Gauge
              className="mt-2"
              value={s.score}
              tone={s.score >= 1 ? "good" : s.score >= 0.5 ? "brand" : "warn"}
            />
            <span className="text-muted-foreground mt-2 text-xs">
              gap <Money amountMinor={s.gap_eur_minor} native="EUR" /> /{" "}
              <Money amountMinor={s.fire_amount_minor} native={s.currency} /> target
            </span>
          </StatCard>
        ))}
      </div>
    </>
  );
}
